import { cloneSlideLayout } from '../components/program/ProgramSlideCanvas';
import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import { genSlideBlockGroupId } from './slideBlockGroups';
import { clonePendingClipboardImageForBlock, isPendingSlideImageSrc } from './slideImagePendingUpload';

function genSlideEntityId(seq: number): string {
  try {
    const c = globalThis.crypto?.randomUUID?.();
    if (c) return c;
  } catch {
    /* ignore */
  }
  return `slide-${Date.now()}-${seq}`;
}

function remapGroupId(
  groupId: string | undefined,
  groupMap: Map<string, string>
): string | undefined {
  if (!groupId) return groupId;
  let next = groupMap.get(groupId);
  if (!next) {
    next = genSlideBlockGroupId();
    groupMap.set(groupId, next);
  }
  return next;
}

function remapBlockId(oldId: string, seq: number, blockIdMap: Map<string, string>): string {
  let next = blockIdMap.get(oldId);
  if (!next) {
    next = genSlideEntityId(seq);
    blockIdMap.set(oldId, next);
  }
  return next;
}

function remapPendingImageSrc(src: string, newBlockId: string): string {
  const trimmed = src.trim();
  if (!trimmed) return src;
  if (!isPendingSlideImageSrc(trimmed)) return src;
  return clonePendingClipboardImageForBlock(trimmed, newBlockId) ?? trimmed;
}

/** 같은 프레젠테이션에 페이지를 복제할 때 블록·그룹·퀴즈 세트 ID를 새로 부여한다. */
export function duplicateSlideLayoutWithFreshIds(layout: ProgramSlideLayoutV1): ProgramSlideLayoutV1 {
  const cloned = cloneSlideLayout(layout);
  const blockIdMap = new Map<string, string>();
  const groupMap = new Map<string, string>();
  const quizSetIdMap = new Map<string, string>();
  let seq = 0;

  const nextBlockId = (oldId: string) => remapBlockId(oldId, (seq += 1), blockIdMap);

  return {
    v: 1,
    appliedLayoutTemplateId: cloned.appliedLayoutTemplateId,
    defaultTextColorHex: cloned.defaultTextColorHex,
    defaultBackgroundColorHex: cloned.defaultBackgroundColorHex,
    pageBackgroundColorHex: cloned.pageBackgroundColorHex,
    pageTextColorHex: cloned.pageTextColorHex,
    boxes: cloned.boxes.map((b) => ({
      ...b,
      id: nextBlockId(b.id),
      groupId: remapGroupId(b.groupId, groupMap),
      textRuns: b.textRuns?.map((r) => ({ ...r })),
    })),
    images: cloned.images.map((img) => {
      const id = nextBlockId(img.id);
      return {
        ...img,
        id,
        groupId: remapGroupId(img.groupId, groupMap),
        crop: { ...img.crop },
      };
    }),
    clipboardImages: (cloned.clipboardImages ?? []).map((img) => {
      const id = nextBlockId(img.id);
      return {
        ...img,
        id,
        src: remapPendingImageSrc(img.src, id),
        groupId: remapGroupId(img.groupId, groupMap),
        crop: { ...img.crop },
      };
    }),
    scanImages: (cloned.scanImages ?? []).map((img) => {
      const id = nextBlockId(img.id);
      return {
        ...img,
        id,
        src: remapPendingImageSrc(img.src, id),
        groupId: remapGroupId(img.groupId, groupMap),
        crop: { ...img.crop },
      };
    }),
    buttons: cloned.buttons.map((btn) => ({
      ...btn,
      id: nextBlockId(btn.id),
      groupId: remapGroupId(btn.groupId, groupMap),
    })),
    shapes: (cloned.shapes ?? []).map((s) => ({
      ...s,
      id: nextBlockId(s.id),
      groupId: remapGroupId(s.groupId, groupMap),
    })),
    polylines: (cloned.polylines ?? []).map((p) => ({
      ...p,
      id: nextBlockId(p.id),
      groupId: remapGroupId(p.groupId, groupMap),
      points: p.points.map((pt) => ({ ...pt })),
    })),
    videos: cloned.videos.map((video) => {
      const id = nextBlockId(video.id);
      return {
        ...video,
        id,
        groupId: remapGroupId(video.groupId, groupMap),
        bookmarks: video.bookmarks.map((bookmark) => ({ ...bookmark })),
      };
    }),
    webpages: (cloned.webpages ?? []).map((wp) => {
      const id = nextBlockId(wp.id);
      let thumbSrc = wp.thumbSrc?.trim() ?? '';
      if (thumbSrc) thumbSrc = remapPendingImageSrc(thumbSrc, id);
      return {
        ...wp,
        id,
        thumbSrc,
        groupId: remapGroupId(wp.groupId, groupMap),
      };
    }),
    quizzes: cloned.quizzes.map((quiz) => ({
      ...quiz,
      id: nextBlockId(quiz.id),
      groupId: remapGroupId(quiz.groupId, groupMap),
    })),
    quizChoiceSets: (cloned.quizChoiceSets ?? []).map((set) => {
      let nextSetId = quizSetIdMap.get(set.id);
      if (!nextSetId) {
        nextSetId = genSlideEntityId((seq += 1));
        quizSetIdMap.set(set.id, nextSetId);
      }
      return {
        ...set,
        id: nextSetId,
        correctOptionIndices: [...(set.correctOptionIndices ?? [])],
      };
    }),
    quizChoiceOptions: (cloned.quizChoiceOptions ?? []).map((opt) => ({
      ...opt,
      id: nextBlockId(opt.id),
      setId: quizSetIdMap.get(opt.setId) ?? opt.setId,
      groupId: remapGroupId(opt.groupId, groupMap),
    })),
  };
}
